import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Label } from '@/shared/components/ui/label';
import { DynamicField } from '@/shared/types/dynamic-fields-types';
import { getLocalizedText } from '@/shared/utils/i18nHelper';

interface DynamicFieldRendererProps {
  field: DynamicField;
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (fieldId: string, value: any) => void;
  disabled?: boolean;
  error?: string;
}

export const DynamicFieldRenderer = ({
  field,
  value,
  onChange,
  disabled,
  error,
}: DynamicFieldRendererProps) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0]; // 'pt-BR' -> 'pt', 'en-US' -> 'en'
  
  const optionalText = currentLang === 'pt' ? '(opcional)' : '(optional)';

  const renderField = () => {
    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <Input
            type={field.type}
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={getLocalizedText(field.placeholder, currentLang)}
            maxLength={field.maxLength}
            min={field.min}
            max={field.max}
            disabled={disabled}
            error={!!error}
            className="h-11"
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={getLocalizedText(field.placeholder, currentLang)}
            maxLength={field.maxLength}
            rows={field.rows || 3}
            disabled={disabled}
            error={!!error}
            className="resize-none"
          />
        );

      case 'select':
        return (
          <Select
            value={value || ''}
            onValueChange={(newValue) => onChange(field.id, newValue)}
            disabled={disabled}
          >
            <SelectTrigger className="h-11" error={!!error}>
              <SelectValue
                placeholder={getLocalizedText(field.placeholder, currentLang)}
              />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    {option.logo_url ? (
                      <img 
                        src={option.logo_url} 
                        alt="" 
                        className="w-4 h-4 object-contain"
                        onError={(e) => {
                          // Fallback para emoji se imagem falhar
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : option.icon ? (
                      <span>{option.icon}</span>
                    ) : null}
                    <span>{getLocalizedText(option.label, currentLang)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'password':
        return (
          <Input
            type="password"
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={getLocalizedText(field.placeholder, currentLang)}
            disabled={disabled}
            error={!!error}
            className="h-11"
          />
        );

      case 'email':
        return (
          <Input
            type="email"
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={getLocalizedText(field.placeholder, currentLang)}
            disabled={disabled}
            error={!!error}
            className="h-11"
          />
        );

      case 'switch':
        return (
          <div className="flex items-center h-11 gap-3">
            <Switch
              checked={!!value}
              onCheckedChange={(checked) => onChange(field.id, checked)}
              disabled={disabled}
            />
            <span className="text-sm text-muted-foreground">
              {value
                ? (currentLang === 'pt' ? 'Sim' : 'Yes')
                : (currentLang === 'pt' ? 'Não' : 'No')
              }
            </span>
          </div>
        );

      case 'multiselect': {
        const currentValues = value || [];
        return (
          <div className="space-y-3">
            {/* Todas as opções como badges clicáveis */}
            <div className="flex flex-wrap gap-2">
              {field.options?.map((option) => {
                const isSelected = currentValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        // Remover
                        onChange(
                          field.id,
                          currentValues.filter((v: string) => v !== option.value)
                        );
                      } else {
                        // Adicionar
                        onChange(field.id, [...currentValues, option.value]);
                      }
                    }}
                    disabled={disabled}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80 border border-border'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {option.logo_url ? (
                      <img
                        src={option.logo_url}
                        alt=""
                        className="w-4 h-4 object-contain flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : option.icon ? (
                      <span className="flex-shrink-0">{option.icon}</span>
                    ) : null}
                    <span>{getLocalizedText(option.label, currentLang)}</span>
                  </button>
                );
              })}
            </div>

            {/* Contador de selecionados */}
            {currentValues.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {currentValues.length} {currentValues.length === 1
                  ? (currentLang === 'pt' ? 'selecionado' : 'selected')
                  : (currentLang === 'pt' ? 'selecionados' : 'selected')
                }
              </p>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-2" data-field-error={error ? "true" : "false"}>
      <Label className="text-sm font-medium">
        {getLocalizedText(field.label, currentLang)}
        {!field.required && (
          <span className="text-muted-foreground text-xs ml-1">
            {optionalText}
          </span>
        )}
        {field.required && <span className="text-destructive/60 ml-1">*</span>}
      </Label>
      <div>{renderField()}</div>
      {field.description && !error && (
        <p className="text-xs text-muted-foreground">
          {getLocalizedText(field.description, currentLang)}
        </p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
